import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Check, X, Save, Sliders, Zap } from "lucide-react";

const TIERS = [{ code: "lite", name: "Lite" }, { code: "growth", name: "Growth" }, { code: "control", name: "Control" }];

export default function PlatformPlans() {
  const [groups, setGroups] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [cfgDraft, setCfgDraft] = useState(null);

  const load = useCallback(() => {
    api.get("/billing/platform/features").then((r) => setGroups(r.data.groups || [])).catch((e) => toast.error(apiErr(e)));
    api.get("/billing/platform/config").then((r) => { setCfg(r.data); setCfgDraft(r.data); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchFeature = async (key, body) => {
    try {
      const { data } = await api.patch(`/billing/platform/features/${key}`, body);
      setGroups((gs) => gs.map((g) => ({ ...g, features: g.features.map((f) => (f.key === key ? { ...f, ...data } : f)) })));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleTier = (f, code) => {
    const tiers = { ...(f.tiers || {}), [code]: !f.tiers?.[code] };
    patchFeature(f.key, { tiers });
  };
  const toggleAddon = (f) => patchFeature(f.key, { addon: !f.addon });
  const setPrice = (f, val) => patchFeature(f.key, { addon_price_sgd: Number(val) });

  const saveConfig = async () => {
    try {
      await api.put("/billing/platform/config", {
        ai_rate_sgd_per_1k_tokens: Number(cfgDraft.ai_rate_sgd_per_1k_tokens),
        ai_free_allowance: {
          lite: Number(cfgDraft.ai_free_allowance.lite), growth: Number(cfgDraft.ai_free_allowance.growth),
          control: Number(cfgDraft.ai_free_allowance.control),
        },
        tax_pct: Number(cfgDraft.tax_pct),
        annual_discount_months: Number(cfgDraft.annual_discount_months),
        auto_billing_enabled: !!cfgDraft.auto_billing_enabled,
        dunning_enabled: !!cfgDraft.dunning_enabled,
      });
      toast.success("Billing configuration saved");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const Toggle = ({ on, onClick, label, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center justify-between gap-2 rounded-sm border px-3 py-2 text-sm transition-colors ${on ? "border-[#00E65B]/40 bg-[#00E65B]/10" : "border-border"}`}>
      <span>{label}</span>
      <span className={`h-4 w-7 rounded-full relative transition-colors ${on ? "bg-[#00E65B]" : "bg-muted"}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${on ? "left-3.5" : "left-0.5"}`} />
      </span>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="platform-plans">
      {/* Billing config */}
      {cfgDraft && (
        <div className="card-flat p-5" data-testid="billing-config">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow inline-flex items-center gap-1"><Sliders className="h-3.5 w-3.5 text-accent" /> Billing configuration</div>
            <Button size="sm" variant="primary" onClick={saveConfig} data-testid="save-config-btn"><Save className="h-3.5 w-3.5" /> Save</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><div className="label-eyebrow text-[10px] mb-1"><Zap className="h-3 w-3 inline" /> AI rate (S$/1K tokens)</div><Input type="number" step="0.001" value={cfgDraft.ai_rate_sgd_per_1k_tokens} onChange={(e) => setCfgDraft((c) => ({ ...c, ai_rate_sgd_per_1k_tokens: e.target.value }))} data-testid="cfg-ai-rate" /></div>
            <div><div className="label-eyebrow text-[10px] mb-1">GST / tax (%)</div><Input type="number" step="0.1" value={cfgDraft.tax_pct} onChange={(e) => setCfgDraft((c) => ({ ...c, tax_pct: e.target.value }))} data-testid="cfg-tax" /></div>
            <div className="hidden sm:block" />
            {TIERS.map((t) => (
              <div key={t.code}><div className="label-eyebrow text-[10px] mb-1">{t.name} free tokens / mo</div>
                <Input type="number" value={cfgDraft.ai_free_allowance?.[t.code]} onChange={(e) => setCfgDraft((c) => ({ ...c, ai_free_allowance: { ...c.ai_free_allowance, [t.code]: e.target.value } }))} data-testid={`cfg-allowance-${t.code}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 items-end">
            <div><div className="label-eyebrow text-[10px] mb-1">Annual discount (months free)</div><Input type="number" min="0" max="6" value={cfgDraft.annual_discount_months} onChange={(e) => setCfgDraft((c) => ({ ...c, annual_discount_months: e.target.value }))} data-testid="cfg-annual-discount" /></div>
            <Toggle on={!!cfgDraft.auto_billing_enabled} onClick={() => setCfgDraft((c) => ({ ...c, auto_billing_enabled: !c.auto_billing_enabled }))} label="Auto-billing (scheduled invoices)" testid="cfg-auto-billing" />
            <Toggle on={!!cfgDraft.dunning_enabled} onClick={() => setCfgDraft((c) => ({ ...c, dunning_enabled: !c.dunning_enabled }))} label="Dunning reminders" testid="cfg-dunning" />
          </div>
        </div>
      )}

      {/* Feature matrix */}
      <div className="card-flat overflow-x-auto" data-testid="feature-matrix">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="p-3">Feature</th>
            {TIERS.map((t) => <th key={t.code} className="p-3 text-center">{t.name}</th>)}
            <th className="p-3 text-center">Add-on</th><th className="p-3 text-right">Price S$/mo</th>
          </tr></thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.group}>
                <tr className="bg-surface-hover/40"><td colSpan={6} className="px-3 py-1.5 label-eyebrow text-[10px] text-accent">{g.group}</td></tr>
                {g.features.map((f) => (
                  <tr key={f.key} className="border-b border-border/40" data-testid={`feature-${f.key}`}>
                    <td className="p-3"><div className="font-medium">{f.name}</div><div className="text-[11px] text-muted-foreground truncate max-w-md">{f.description}</div></td>
                    {TIERS.map((t) => (
                      <td key={t.code} className="p-3 text-center">
                        <button onClick={() => toggleTier(f, t.code)} data-testid={`tier-${f.key}-${t.code}`}
                          className={`h-6 w-6 rounded-sm grid place-items-center transition-colors ${f.tiers?.[t.code] ? "bg-[#00E65B]/20 text-[#00E65B] border border-[#00E65B]/40" : "border border-border text-muted-foreground hover:bg-surface-hover"}`}>
                          {f.tiers?.[t.code] ? <Check className="h-3.5 w-3.5" /> : <X className="h-3 w-3" />}
                        </button>
                      </td>
                    ))}
                    <td className="p-3 text-center">
                      <button onClick={() => toggleAddon(f)} data-testid={`addon-toggle-${f.key}`}
                        className={`px-2 py-0.5 rounded-full text-[10px] transition-colors ${f.addon ? "bg-accent/15 text-accent border border-accent/30" : "border border-border text-muted-foreground"}`}>
                        {f.addon ? "Sellable" : "No"}
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      {f.addon ? (
                        <Input type="number" defaultValue={f.addon_price_sgd ?? ""} onBlur={(e) => e.target.value !== String(f.addon_price_sgd ?? "") && setPrice(f, e.target.value)}
                          className="h-7 w-24 text-right font-mono ml-auto" data-testid={`price-${f.key}`} />
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
